import type { Archive, Unarchive } from '@trello/action-history';
import { featureFlagClient } from '@trello/feature-flag-client';
import { client } from '@trello/graphql';
import { forNamespace } from '@trello/i18n';
import type { Board } from '@trello/model-types';

import type { Card as LegacyCardModel } from 'app/scripts/models/Card';
import type { Trace } from '../ActionMapTypes';
import { CardActionFragmentDoc } from '../CardActionFragment.generated';
import { CardActionDocument } from '../CardActionMutation.generated';
import { NoopError } from '../NoopError';
import { onTracedCardUpdate } from '../onTracedCardUpdate';
import { recordCardAction } from '../recordCardAction';

const format = forNamespace('notificationsGrouped', {
  shouldEscapeStrings: false,
});

export const archive = (
  card: LegacyCardModel,
  action: Archive,
  trace: Trace,
) => {
  const useGraphQlActions = featureFlagClient.get(
    'xf.undo-redo-rewrite',
    false,
  );

  const cardId = card.id;
  const { traceId } = trace;

  if (useGraphQlActions) {
    const cardFragment = client.readFragment({
      id: `Card:${cardId}`,
      fragment: CardActionFragmentDoc,
    });

    if (cardFragment.closed) {
      throw new NoopError();
    }

    const boardId = cardFragment.idBoard;

    recordCardAction(cardId, action);

    // don't await so we don't block the UI
    client.mutate({
      mutation: CardActionDocument,
      variables: {
        cardId,
        card: {
          closed: true,
        },
        traceId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCard: {
          __typename: 'Card',
          ...cardFragment,
          id: cardId,
          closed: true,
        },
      },
      update: (cache, { data: optimisticData }) => {
        if (!optimisticData?.updateCard) {
          return;
        }

        // Filter out the closed card from the board.cards array
        cache.modify<Board>({
          id: cache.identify({
            id: boardId,
            __typename: 'Board',
          }),
          fields: {
            cards(existingCards = [], { readField }) {
              const updatedCards = existingCards.filter((cardRef) => {
                return readField('id', cardRef) !== cardId;
              });
              return updatedCards;
            },
          },
        });
      },
    });
  } else {
    if (card.get('closed')) {
      throw new NoopError();
    }
    card.close(traceId, onTracedCardUpdate({ card, ...trace }));
  }

  return format('notification_archived_card');
};

export const unArchive = (
  card: LegacyCardModel,
  action: Unarchive,
  trace: Trace,
) => {
  const useGraphQlActions = featureFlagClient.get(
    'xf.undo-redo-rewrite',
    false,
  );

  const cardId = card.id;
  const { traceId } = trace;

  if (useGraphQlActions) {
    const cardFragment = client.readFragment({
      id: `Card:${cardId}`,
      fragment: CardActionFragmentDoc,
    });

    if (!cardFragment.closed) {
      throw new NoopError();
    }

    recordCardAction(cardId, action);

    // don't await so we don't block the UI
    client.mutate({
      mutation: CardActionDocument,
      variables: {
        cardId,
        card: {
          closed: false,
        },
        traceId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCard: {
          __typename: 'Card',
          ...cardFragment,
          id: cardId,
          closed: false,
        },
      },
    });
  } else {
    if (!card.get('closed')) {
      throw new NoopError();
    }
    // @ts-expect-error
    card.reopen(traceId, onTracedCardUpdate({ card, ...trace }));
  }

  return format('notification_unarchived_card');
};
